import React, { ReactElement, ReactNode } from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import styled from 'styled-components';
import { down, up } from '../../utils/styles/breakpoints';
import { px } from '../../utils/styles/px';
import { useTranslation } from '../../utils/useTranslation';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { Logo, LogoCompact } from './Logo';
import { CONTENT_WIDTH } from './constants';

const TABLET_MENU_WIDTH = 88;

export const DESKTOP_MENU_WIDTH = 316;

const Aside = styled(Box).attrs({
    px: 1,
    py: { xs: 1, sm: 0 },
    width: { xs: '100%', sm: TABLET_MENU_WIDTH, lg: DESKTOP_MENU_WIDTH },
})`
    padding-bottom: calc(8px + var(--safe-area-inset-bottom, 0));
    box-sizing: border-box;
    position: fixed;
    bottom: 0;
    left: 0;
    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.12) inset;
    background: color-mix(in srgb, var(--background-primary) 65%, transparent);
    -webkit-backdrop-filter: blur(var(--blur-radius-l));
    backdrop-filter: blur(var(--blur-radius-l));
    transform: translate3d(0, 0, 0);

    ${up('sm')} {
        top: 0;
        background: none;
        backdrop-filter: none;
        box-shadow: none;
    }
`;

const Footer = styled(Flex)`
    position: fixed;
    left: 0;
    bottom: 0;
    max-width: calc((100% - ${CONTENT_WIDTH}px) / 2);
    ${down('md')} {
        display: none;
    }
`;

const DashboardMenuItemRoot = styled(Flex).attrs({
    spacing: 2,
    direction: 'row',
    alignItems: 'center',
    width: { xs: 'auto', sm: '100%' },
    p: { xs: 1, sm: 1.5 },
})`
    text-decoration: none;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-delay: 0ms;
    transition-property: color, transform;
    color: var(--color-secondary);
    cursor: pointer;

    &:hover,
    &:focus,
    &:active,
    &.active {
        color: var(--color-primary);
    }
`;

interface DashboardMenuItemProps extends NavLinkProps {
    variant?: 'flat' | 'button';
    selectedIcon: ReactElement;
    unselectedIcon: ReactElement;
    title: string;
    subtitle?: string;
}

function DashboardMenuItem(props: DashboardMenuItemProps) {
    const { selectedIcon, unselectedIcon, title, subtitle, ...other } = props;
    const t = useTranslation();

    return (
        <DashboardMenuItemRoot as={NavLink} {...other}>
            {({ isActive }) => (
                <>
                    <Flex fontSize={24}>{isActive ? selectedIcon : unselectedIcon}</Flex>
                    <Box display={{ xs: 'none', lg: 'initial' }}>
                        <Typography variant="body" color="inherit" weight="bold">
                            {t(title)}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="inherit">
                                {t(subtitle)}
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </DashboardMenuItemRoot>
    );
}

const Content = styled(Box).attrs({
    display: 'flex',
    width: '100%',
    minHeight: '100%',
    pl: { sm: px(TABLET_MENU_WIDTH), lg: px(DESKTOP_MENU_WIDTH) },
    pr: { sm: px(TABLET_MENU_WIDTH), lg: 0 },
})`
    --safe-area-inset-bottom: calc(56px + env(safe-area-inset-bottom, 0));

    ${up('sm')} {
        --safe-area-inset-bottom: env(safe-area-inset-bottom, 0);
    }
`;

interface DashboardLayoutProps {
    menuItems: DashboardMenuItemProps[];
    children: ReactNode;
}

//
export function DashboardLayout(props: DashboardLayoutProps) {
    const { children, menuItems } = props;
    const t = useTranslation();

    return (
        <Box
            display="flex"
            width="100%"
            position="relative"
            minHeight="100vh"
            pb={{ xs: 'calc(56px + var(--safe-area-inset-bottom))', sm: 0 }}
        >
            <Content>{children}</Content>
            <Aside>
                <Box pt={2.5} pb={0.5} px={2.5} display={{ xs: 'none', lg: 'block' }}>
                    <Logo height={64} />
                </Box>
                <Box pt={3} pb={1} px={1} display={{ xs: 'none', sm: 'block', lg: 'none' }}>
                    <LogoCompact height={56} />
                </Box>
                <Flex
                    px={1}
                    width="100%"
                    direction={{ xs: 'row', sm: 'column' }}
                    justifyContent={{ xs: 'space-between', sm: 'flex-start' }}
                >
                    {menuItems.map((menuItem, index) => (
                        <DashboardMenuItem {...menuItem} key={index} role="menuitem" />
                    ))}
                </Flex>
            </Aside>

            <Footer p={3} spacing={0.75}>
                <Typography
                    as="a"
                    href="https://yandex.ru/support2/rythm/ru/"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('О сервисе')}
                </Typography>
                <Typography
                    as="a"
                    href="https://yandex.ru/legal/ritm_termsofuse"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Пользовательское соглашение')}
                </Typography>
                <Typography
                    as="a"
                    href="https://yandex.ru/legal/confidential"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Политика конфиденциальности')}
                </Typography>
                <Typography
                    as="a"
                    href="https://yandex.ru/legal/recommendations"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Правила рекомендательных технологий')}
                </Typography>
                <Typography
                    as="a"
                    href="https://apps.apple.com/app/яндекс-ритм/id6477834713"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Приложение в App Store')}
                </Typography>
                <Typography
                    as="a"
                    href="https://play.google.com/store/apps/details?id=com.yandex.rhythm"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Приложение в Google play')}
                </Typography>
                <Typography
                    as="a"
                    href="https://appgallery.huawei.ru/app/C110487727"
                    target="_blank"
                    variant="caption"
                    weight="regular"
                    color="secondary"
                >
                    {t('Приложение в AppGallery')}
                </Typography>
            </Footer>
        </Box>
    );
}
